import { Button, filterEmpty, MuiAlert, strIsNullOrEmpty, tryParseJSONObject, useRenderer } from '@fox/atoms';
import { IBlockDefinition, IBlockProps } from '@fox/blocks';
import { CentredFlexRow, SpaceEvenRow } from '@fox/layout';
import { useI18n, useSubscribeTopicWithHandler } from 'fox-extensions';
import React, { useState } from 'react';
import { RSVP_BLOCK_TYPE } from '../../../form-block-types';
import { EventCapacityStatus, RSVP_WAITLIST_ANSWER, RSVPAnswer, useFormEventContext } from '../../form-event-context';
import { IQuestionAnswer } from '../../form-root/contracts';
import { FORM_QUESTION_ANSWERED_TOPIC, FormRenderMode, useFormRuntimeContext } from '../../form-runtime-context';
import { FormButtonWrapper } from '../../styled/form-button-wrapper';
import { ButtonBlock } from '../button-block/button-block';
import { ButtonBlockProps } from '../button-block/contracts';
import { checkAllowNext } from '../form-block/util/allow-next';
import { ensureAnswers } from '../form-block/util/ensure-answers';
import { RSVPBlockMetadata } from './contracts';

export const RSVPComponent = ({
	id,
	props,
	type
}: IBlockProps<ButtonBlockProps & { metadataJson?: string }> & {
	additionalEditUI?: JSX.Element;
	showHoveredSelection?: boolean;
	showSelectedSelection?: boolean;
}) => {
	const { setWaitlistSubmitted } = useFormEventContext();
	const { submitForm, formRenderMode, currentPage: formCurrentPage, sections, layout, getAnswers } = useFormRuntimeContext();
	const eventContext = useFormEventContext();
	const render = useRenderer();
	useSubscribeTopicWithHandler(FORM_QUESTION_ANSWERED_TOPIC, () => {
		render();
	});
	const i18n = useI18n();
	const [loading, setLoading] = useState(false);
	const metaData = tryParseJSONObject<RSVPBlockMetadata>(props.metadataJson, {});
	const selectedRsvpAction = metaData?.selectedRsvpAction;
	const status = eventContext?.event?.status;
	const onWaitList = status === EventCapacityStatus.OnWaitlist;
	const currentPage = sections[formCurrentPage ?? 0];
	const allowNext = checkAllowNext({ formRenderMode, currentPage, waitlistSubmit: onWaitList, layout, sections, getAnswers });
	if (status === EventCapacityStatus.Closed || status === EventCapacityStatus.Full) {
		return (
			<SpaceEvenRow>
				<MuiAlert severity="warning">Sorry! This event is {status.toLowerCase()}!</MuiAlert>
			</SpaceEvenRow>
		);
	}

	const handleRsvpClick = async (e: React.MouseEvent) => {
		e.preventDefault();
		const answers = getAnswers();
		if (formRenderMode === FormRenderMode.design || !allowNext || strIsNullOrEmpty(selectedRsvpAction)) {
			return;
		}

		const action = tryParseJSONObject<RSVPAnswer>(selectedRsvpAction, {});
		const rsvp: RSVPAnswer = {
			id: onWaitList ? RSVP_WAITLIST_ANSWER : action?.id,
			count: eventContext?.event?.reservation?.count ?? action?.count
		};

		const answer = {
			questionName: 'rsvp',
			answer: {
				answerJson: JSON.stringify(rsvp)
			}
		};

		let finalAnswers: IQuestionAnswer<Record<string, unknown>>[] = [];

		const exists = answers?.find(a => a.questionName === 'rsvp');
		if (exists) {
			finalAnswers = filterEmpty(
				(answers ?? [])?.map(a => {
					return a.questionName === 'rsvp' ? answer : a;
				})
			);
		} else {
			finalAnswers = [...(answers ?? []), answer];
		}
		const toSubmit = await ensureAnswers(finalAnswers, i18n.countryCode);
		await submitForm?.(toSubmit);
	};

	const handleClick = async (event: React.MouseEvent) => {
		setLoading(true);
		// TODO: This should be handled via SNF on submission
		// Currently this just creates RSVP even if submission fails

		if (status === EventCapacityStatus.Available || onWaitList) {
			// In case page is stale, validate RSVP server-side
			const isValid = await eventContext.validateRsvpResponse();
			if (!isValid) return;
		}
		await handleRsvpClick(event);
		setLoading(false);
	};

	if (onWaitList) {
		return (
			<React.Fragment>
				<SpaceEvenRow>
					<MuiAlert severity="warning">Sorry! This event is full! Register for the waitlist:</MuiAlert>
				</SpaceEvenRow>
				<CentredFlexRow style={{ padding: '1rem' }}>
					<Button
						variant="primary"
						disabled={(!eventContext?.event?.reservation && !eventContext?.event?.ticketsEnabled) || loading}
						onClick={async e => {
							await handleClick(e);
							setWaitlistSubmitted(true);
						}}
					>
						Register For Waitlist
					</Button>
				</CentredFlexRow>
			</React.Fragment>
		);
	}
	//we need to update meta with all the available options
	return (
		<FormButtonWrapper
			$disabled={
				((!allowNext || loading) && formRenderMode !== FormRenderMode.design) ||
				(status === EventCapacityStatus.Available && !eventContext?.event?.reservation && !eventContext?.event?.ticketsEnabled)
			}
			onClick={handleClick}
			id="rsvpbutton"
		>
			<ButtonBlock id={id} type={type} props={props} />
		</FormButtonWrapper>
	);
};

/**
 * Runtime registration for the RSVP block.
 *
 * This block provides RSVP functionality for forms, allowing users to respond to event invitations.
 * It supports both standard RSVP and waitlist scenarios, and handles form submission logic accordingly.
 *
 * Use this block for all RSVP button requirements in forms.
 */
export const RSVPBlockRuntime: IBlockDefinition<ButtonBlockProps & { metadataJson: string }> = {
	type: RSVP_BLOCK_TYPE,
	Component: RSVPComponent
};
